import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { of, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { WordpressAuthService } from './wordpress-auth.service';
import { DictionaryService } from './dictionary.service';
import { WordClickService } from '@app/services/word-click.service';

@Component({
  selector: 'app-dictionary-lookup',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './dictionary-lookup.component.html',
  styleUrls: ['./dictionary-lookup.component.scss']
})
export class DictionaryLookupComponent implements OnInit, OnDestroy {
  term = '';
  language = 'en'; // Will be updated with current language
  stores: any[] = [];
  selectedStores = new Set<string>();
  results: any[] = [];
  loading = false;
  noResultsFound = false;

  private _destroy$ = new Subject<void>();

  constructor(
    private auth: WordpressAuthService,
    private dictionary: DictionaryService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private _wordClickService: WordClickService,
  ) {}

  ngOnInit() {
    // Set the language to the current user's selected language
    this.language = this.translateService.currentLang || 'en';
    console.log('Dictionary lookup initialized with language:', this.language);
    
    // Listen for language changes
    this.translateService.onLangChange.subscribe((event) => {
      this.language = event.lang;
      console.log('Dictionary lookup language changed to:', this.language);
    });

    this.dictionary.getStores().subscribe((stores) => {
      this.stores = stores;
      stores.forEach((s) => {
        if (s.selected_by_default) this.selectedStores.add(s.title);
      });
    });

    // When a word is clicked in step content the WordClickService broadcasts it here.
    // Pre-fill the search field and run the lookup immediately, then clear the signal
    // so it doesn't re-fire if the component is destroyed and re-created.
    this._wordClickService.word$
      .pipe(filter(w => !!w), takeUntil(this._destroy$))
      .subscribe(word => {
        this.term = word;
        this.cdr.detectChanges();
        this.lookup();
        this._wordClickService.clear();
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  toggleStore(title: string) {
    this.selectedStores.has(title) ? this.selectedStores.delete(title) : this.selectedStores.add(title);
  }

  lookup() {
    if (!this.term) return;

    this.loading = true;
    this.results = [];
    this.noResultsFound = false;

    const selectedSources = this.stores.filter((s) => this.selectedStores.has(s.title)).map((s) => s.source);

    console.log('Dictionary lookup for term:', this.term, 'with language:', this.language);

    this.dictionary.lookup(this.term, this.language, selectedSources).subscribe({
      next: (res) => {
        console.log('Raw API response:', res);
        
        // Handle different possible response structures
        let defs = null;
        
        if (res && Array.isArray(res.definitions)) {
          defs = res.definitions;
        } else if (res && Array.isArray(res)) {
          defs = res;
        } else if (res && typeof res === 'object') {
          // Try to find definitions in the object
          defs = res.definitions || res.results || res.data || Object.values(res);
        }
        
        console.log('Extracted definitions:', defs);
        
        if (Array.isArray(defs) && defs.length > 0) {
          this.results = defs.map(def => this.formatDefinition(def));
          console.log('Processed results:', this.results);
        } else {
          this.results = [];
          console.log('No definitions found, setting empty array');
        }
        
        // Check if no results were found
        this.noResultsFound = this.results.length === 0;
        console.log('Results length:', this.results.length, 'noResultsFound:', this.noResultsFound);
        
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Lookup error:', err);
        this.loading = false;
        this.noResultsFound = true;
      },
    });
  }

  trackByDef(index: number, def: any): string {
    return def.source + '-' + def.term; // or a UUID if available
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private formatDefinition(def: any): any {
    // Create a copy to avoid modifying the original
    const formattedDef = { ...def };
    
    // Format the definition text
    if (formattedDef.definition) {
      formattedDef.definition = this.cleanAndFormatText(formattedDef.definition);
    }
    
    // Format the HTML content
    if (formattedDef.html) {
      formattedDef.html = this.cleanAndFormatHtml(formattedDef.html);
    }
    
    // Special handling for etymology results
    if (formattedDef.source === 'dwds-etymology') {
      formattedDef.definition = this.formatEtymology(formattedDef.definition);
    }
    
    return formattedDef;
  }

  private cleanAndFormatText(text: string): string {
    if (!text) return '';
    
    // Decode HTML entities
    let cleaned = this.decodeHtmlEntities(text);
    
    // Remove HTML tags but keep their content
    cleaned = cleaned.replace(/<[^>]*>/g, '');
    
    // Clean up extra whitespace
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    
    return cleaned;
  }

  private cleanAndFormatHtml(html: string): string {
    if (!html) return '';
    
    // Decode HTML entities
    let cleaned = this.decodeHtmlEntities(html);
    
    // Remove "Part of Speech: unknown" lines
    cleaned = cleaned.replace(/<div[^>]*><em>Part of Speech:<\/em> unknown<\/div>/g, '');
    cleaned = cleaned.replace(/Part of Speech: unknown/g, '');
    
    // Replace common acronym tags with more readable format
    cleaned = cleaned.replace(/<acronym[^>]*title="([^"]*)"[^>]*>([^<]*)<\/acronym>/g, '<span class="acronym" title="$1">$2</span>');
    
    // Replace target spans with better formatting
    cleaned = cleaned.replace(/<span class="target">\[([^<]*)<span class="or"><acronym[^>]*>or<\/acronym><\/span>([^<]*)\]<\/span>/g, '<span class="alternative">[$1 or $2]</span>');
    
    // Replace style spans
    cleaned = cleaned.replace(/<span class="style"><acronym[^>]*title="([^"]*)"[^>]*>([^<]*)<\/acronym><\/span>/g, '<span class="style" title="$1">$2</span>');
    
    // Replace age spans
    cleaned = cleaned.replace(/<span class="age">([^<]*)<\/span>/g, '<span class="age">$1</span>');
    
    // Clean up any remaining HTML entities
    cleaned = cleaned.replace(/&[^;]+;/g, (match) => {
      const entityMap: { [key: string]: string } = {
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&quot;': '"',
        '&#39;': "'",
        '&apos;': "'"
      };
      return entityMap[match] || match;
    });
    
    return cleaned;
  }

  private formatEtymology(text: string): string {
    if (!text) return '';
    
    // Split the long etymology text into more readable sections
    let formatted = text;
    
    // Add line breaks before numbered sections
    formatted = formatted.replace(/(\d+\.)/g, '\n\n$1');
    
    // Add line breaks before Greek letters (α, β, γ, etc.)
    formatted = formatted.replace(/([α-ω])/g, '\n$1');
    
    // Add line breaks before "Beispiele:"
    formatted = formatted.replace(/(Beispiele:)/g, '\n$1');
    
    // Add line breaks before "Beispiel:"
    formatted = formatted.replace(/(Beispiel:)/g, '\n$1');
    
    // Add line breaks before "umgangssprachlich"
    formatted = formatted.replace(/(umgangssprachlich)/g, '\n$1');
    
    // Add line breaks before "veraltend"
    formatted = formatted.replace(/(veraltend)/g, '\n$1');
    
    // Add line breaks before "sprichwörtlich"
    formatted = formatted.replace(/(sprichwörtlich)/g, '\n$1');
    
    // Clean up multiple line breaks
    formatted = formatted.replace(/\n{3,}/g, '\n\n');
    
    return formatted;
  }

  private decodeHtmlEntities(text: string): string {
    if (!text) return '';
    
    // Create a temporary element to decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  getDisplayContent(def: any): string {
    // Prefer HTML content if available, otherwise use definition
    const content = def.html || def.definition || '';
    return this.cleanAndFormatText(content);
  }

  isEtymology(def: any): boolean {
    return def.source === 'dwds-etymology';
  }
}
